import React, { Fragment, useEffect, useReducer } from "react";
import PropTypes from "prop-types";
import { Button, InputGroup, Form } from "react-bootstrap";
import { MARKETPLACE_CONSTANTS } from "../../../config/constants";
import { colors } from "../../../config/colors";
import { Typography } from "@mui/material";
import { TEXT_ALIGN_LEFT } from "../../../config/styles";
import { WarningLogo } from "../../../assets/icons";

// Action types
const ACTION_TYPES = {
  SET_FIELD: "SET_FIELD",
  SET_ERROR: "SET_ERROR",
  CLEAR_ERROR: "CLEAR_ERROR",
  RESET_FORM: "RESET_FORM",
};

// Initial state
const initialState = {
  title: "",
  value: "",
  titleAr: "",
  valueAr: "",
  hasError: {},
};

// Reducer function
const specificationsReducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.SET_FIELD:
      return {
        ...state,
        [action.fieldName]: action.value,
        hasError: {
          ...state.hasError,
          [action.fieldName]: null, // Clear error for this field
        },
      };

    case ACTION_TYPES.SET_ERROR:
      return {
        ...state,
        hasError: {
          ...state.hasError,
          [action.fieldName]: action.error,
        },
      };

    case ACTION_TYPES.CLEAR_ERROR:
      return {
        ...state,
        hasError: {
          ...state.hasError,
          [action.fieldName]: null,
        },
      };

    case ACTION_TYPES.RESET_FORM:
      return {
        ...initialState,
      };

    default:
      return state;
  }
};

const Specifications = ({
  onDelete = () => {},
  initialValues,
  onSave = () => {},
}) => {
  const [state, dispatch] = useReducer(specificationsReducer, initialState);

  const handleSave = () => {
    const { title, value, titleAr, valueAr } = state;
    if (!title || !value || !titleAr || !valueAr) {
      !title && setError("title", "Please enter the Title.");
      !value &&
        setError(
          "value",
          "Please enter the Value. Example: AED 3,000 or 12 months."
        );
      !titleAr && setError("titleAr", "Please enter the Title.");
      !valueAr &&
        setError(
          "valueAr",
          "Please enter the Value. Example: 3000 درهم إماراتي أو 12 شهرًا."
        );
      return;
    } else {
      onSave({
        title: title,
        value: value,
        titleAr: titleAr,
        valueAr: valueAr,
      });
      resetForm();
    }
  };

  useEffect(() => {
    if (initialValues) {
      dispatch({
        type: ACTION_TYPES.SET_FIELD,
        fieldName: "title",
        value: initialValues.title || "",
      });
      dispatch({
        type: ACTION_TYPES.SET_FIELD,
        fieldName: "value",
        value: initialValues.value || "",
      });
      dispatch({
        type: ACTION_TYPES.SET_FIELD,
        fieldName: "titleAr",
        value: initialValues.titleAr || "",
      });
      dispatch({
        type: ACTION_TYPES.SET_FIELD,
        fieldName: "valueAr",
        value: initialValues.valueAr || "",
      });
    }
  }, [initialValues]);

  const specificationFields = [
    {
      id: "title",
      label: MARKETPLACE_CONSTANTS.TITLE_LABEL,
      type: "text",
      placeholder: MARKETPLACE_CONSTANTS.TITLE_LABEL,
      value: state.title,
      additionalText: "",
    },
    {
      id: "value",
      label: MARKETPLACE_CONSTANTS.DESCRIPTION_LABEL,
      type: "text",
      placeholder: MARKETPLACE_CONSTANTS.DESCRIPTION_LABEL,
      value: state.value,
      additionalText: MARKETPLACE_CONSTANTS.DESCRIPTION_SAMPLE,
    },
    {
      id: "titleAr",
      label: MARKETPLACE_CONSTANTS.TITLE_AR_LABEL,
      type: "text",
      placeholder: MARKETPLACE_CONSTANTS.TITLE_AR_LABEL,
      value: state.titleAr,
      additionalText: "",
    },
    {
      id: "valueAr",
      label: MARKETPLACE_CONSTANTS.DESCRIPTION_AR_LABEL,
      type: "text",
      placeholder: MARKETPLACE_CONSTANTS.DESCRIPTION_AR_LABEL,
      value: state.valueAr,
      additionalText: MARKETPLACE_CONSTANTS.DESCRIPTION_AR_SAMPLE,
    },
  ];

  const handleFieldChange = (event) => {
    const { id, value } = event.target;
    dispatch({
      type: ACTION_TYPES.SET_FIELD,
      fieldName: id,
      value: value,
    });
  };

  const setError = (fieldName, error) => {
    dispatch({
      type: ACTION_TYPES.SET_ERROR,
      fieldName,
      error,
    });
  };

  const resetForm = () => {
    dispatch({
      type: ACTION_TYPES.RESET_FORM,
    });
  };

  return (
    <div className="specification-box">
      {specificationFields.map((field) => {
        return (
          <Fragment key={field.id}>
            <InputGroup
              hasValidation
              className="mx-1 w-100 mb-1"
              controlId={field.id}
            >
              <Form.Floating className="flex-grow-1">
                <Form.Control
                  id={field.id}
                  type={field.type}
                  required
                  autoComplete="off"
                  placeholder={field.placeholder}
                  value={field.value}
                  onChange={(e) => {
                    setError(field.id);
                    handleFieldChange(e);
                  }}
                  onSelect={(e) => {
                    setError(field.id);
                    handleFieldChange(e);
                  }}
                />
                <label htmlFor={field.id}>
                  <span>{field.label}</span>
                  <span style={{ color: colors.warning }}> *</span>
                </label>
              </Form.Floating>
            </InputGroup>
            {state.hasError[field.id] ? (
              <div
                className="mb-3 mx-1 w-100 d-flex gap-2"
                style={{ color: colors.warning, textAlign: TEXT_ALIGN_LEFT }}
              >
                <WarningLogo />
                {state.hasError[field.id]}
              </div>
            ) : (
              <Typography
                variant="subtitle2"
                className="mx-1 mb-3 w-100"
                sx={{ color: colors.grayLight, textAlign: TEXT_ALIGN_LEFT }}
              >
                {field.additionalText}
              </Typography>
            )}
          </Fragment>
        );
      })}
      <div className=" d-flex flex-row gap-3 w-100">
        <Button
          className="cancel-button w-100"
          onClick={() => {
            resetForm();
            onDelete();
          }}
        >
          Delete
        </Button>

        <Button
          className="generate-button w-100"
          onClick={handleSave}
          disabled={
            !state.title ||
            !state.titleAr ||
            !state.value ||
            !state.valueAr
          }
        >
          Done
        </Button>
      </div>
    </div>
  );
};

Specifications.propTypes = {
  onDelete: PropTypes.func,
  initialValues: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    titleAR: PropTypes.string,
    descriptionAR: PropTypes.string,
  }),
  onSave: PropTypes.func.isRequired,
};

export default Specifications;
